import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, getUser } from '../api';
import { formatDate, clienteNome, ConfirmDialog, LoadingSpinner } from '../components/shared';

const STATI = [
  { key:'APERTA',     label:'Aperta',     color:'var(--c-red)',   pill:'pill-red'  },
  { key:'IN_CORSO',   label:'In corso',   color:'var(--c-amber)', pill:'pill-amber'},
  { key:'COMPLETATA', label:'Completata', color:'var(--c-green)', pill:'pill-green'},
  { key:'SOSPESA',    label:'Sospesa',    color:'var(--text-faint)', pill:'pill-gray'},
];
function statoInfo(s) { return STATI.find(x => x.key === s) || { label:s, color:'var(--text-faint)', pill:'pill-gray' }; }
function diffGiorni(d) { if(!d) return null; return Math.round((new Date(d)-new Date())/(1000*60*60*24)); }

// ─── PAGINA NUOVA / MODIFICA ATTIVITÀ (sostituisce il popup) ─────────────────
function authHeaders() {
  let t = '';
  try { t = localStorage.getItem('sp_token') || ''; } catch (e) { /* storage non disponibile */ }
  return { Authorization: 'Bearer ' + t };
}

function urlAllegato(f) {
  if (f.url) return f.url;
  const fn = f.filename || '';
  if (fn && !fn.includes('.') && fn.length > 20) return `https://drive.google.com/file/d/${fn}/view`;
  return `/uploads/${fn}`;
}

function nomeAllegato(f) {
  return f.nome || (f.filename || '').split('/').pop().replace(/^\d+_/, '') || 'file';
}

const ATT_VUOTA = {
  id_cliente: '', titolo: '', descrizione: '', assegnata_a: '', id_assegnato: '',
  data_inizio: new Date().toISOString().slice(0, 10),
  data_scad: '', perc_compl: 0, stato: 'APERTA', note: '',
};

const STATI_SOTTO = [
  { key: 'APERTA',     label: 'Aperta' },
  { key: 'IN_CORSO',   label: 'In corso' },
  { key: 'COMPLETATA', label: 'Completata' },
];

function SelectUtente({ value, utenti, onChange, disabled, style }) {
  return (
    <select className="form-control" value={value || ''} disabled={disabled} style={style}
      onChange={e => onChange(e.target.value)}>
      <option value="">— Nessuno —</option>
      {utenti.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
    </select>
  );
}

const GRIGLIA_SOTTO = '24px minmax(0,1fr) 150px 128px 110px 28px';

// Riga di una sottoattività già salvata. gestore = può cambiare tutto; altrimenti solo lo stato.
function SottoRiga({ s, utenti, gestore, onSalva, onElimina }) {
  const [titolo, setTitolo] = useState(s.titolo);
  useEffect(() => { setTitolo(s.titolo); }, [s.titolo]);
  const fatta = s.stato === 'COMPLETATA';
  const dg = diffGiorni(s.data_scad);
  const scaduta = !fatta && dg !== null && dg < 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: GRIGLIA_SOTTO, gap: 8, alignItems: 'center',
      padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <input type="checkbox" checked={fatta} disabled={!s.puo_stato} title="Segna come completata"
        style={{ accentColor: 'var(--accent)' }}
        onChange={e => onSalva(s, { stato: e.target.checked ? 'COMPLETATA' : 'APERTA' })} />
      <input className="form-control" value={titolo} disabled={!gestore}
        onChange={e => setTitolo(e.target.value)}
        onBlur={() => {
          const t = titolo.trim();
          if (t && t !== s.titolo) onSalva(s, { titolo: t }); else setTitolo(s.titolo);
        }}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
        style={{ textDecoration: fatta ? 'line-through' : 'none', color: fatta ? 'var(--text-faint)' : undefined }} />
      <SelectUtente value={s.id_assegnato} utenti={utenti} disabled={!gestore}
        onChange={v => onSalva(s, { id_assegnato: v || null })} />
      <input className="form-control" type="date" value={s.data_scad || ''} disabled={!gestore}
        style={scaduta ? { borderColor: 'var(--c-red)' } : undefined}
        onChange={e => onSalva(s, { data_scad: e.target.value || null })} />
      <select className="form-control" value={s.stato} disabled={!s.puo_stato}
        onChange={e => onSalva(s, { stato: e.target.value })}>
        {STATI_SOTTO.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
      </select>
      {gestore
        ? <button type="button" className="btn btn-sm btn-danger btn-icon" title="Elimina" onClick={() => onElimina(s)}>×</button>
        : <span />}
    </div>
  );
}

export function AttivitaDettaglio({ addToast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const nuova = !id;
  const io = getUser();

  const [d, setD]                 = useState(ATT_VUOTA);
  const [clienti, setClienti]     = useState([]);
  const [utenti, setUtenti]       = useState([]);
  const [loading, setLoading]     = useState(!nuova);
  const [errore, setErrore]       = useState('');
  const [saving, setSaving]       = useState(false);
  const [puoModificare, setPuoModificare] = useState(true);
  const [sotto, setSotto]         = useState([]);          // sottoattività salvate (modifica)
  const [sottoInAttesa, setSottoInAttesa] = useState([]);  // sottoattività inserite prima del primo salvataggio
  const [nuovaSotto, setNuovaSotto] = useState({ titolo: '', id_assegnato: '', data_scad: '' });
  const [allegati, setAllegati]   = useState([]);
  const [inAttesa, setInAttesa]   = useState([]);          // file scelti prima del primo salvataggio
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver]   = useState(false);
  const [confermaDel, setConfermaDel] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const bloccato = !nuova && !puoModificare;   // vede l'attività solo per una sua sottoattività

  useEffect(() => {
    api.getClienti({ limit: 300 })
      .then(r => setClienti(Array.isArray(r) ? r : (r?.rows || [])))
      .catch(() => {});
  }, []);

  // Utenti assegnabili; in una nuova attività l'assegnatario predefinito è chi la crea
  useEffect(() => {
    api.get('/utenti-elenco').then(lista => {
      const l = Array.isArray(lista) ? lista : [];
      setUtenti(l);
      if (nuova && io?.id) {
        const me = l.find(u => String(u.id) === String(io.id));
        if (me) setD(p => (p.id_assegnato ? p : { ...p, id_assegnato: String(me.id), assegnata_a: me.nome }));
      }
    }).catch(() => {});
  }, [nuova]);

  const caricaSotto = useCallback(async () => {
    if (nuova) return;
    try {
      const r = await api.get(`/attivita/${id}`);
      setSotto(r.sottoattivita || []);
    } catch (e) { /* resta l'elenco precedente */ }
  }, [id, nuova]);

  useEffect(() => {
    if (nuova) return;
    let attivo = true;
    (async () => {
      try {
        const r = await api.get(`/attivita/${id}`);
        if (!attivo) return;
        const { sottoattivita, puo_modificare, ...att } = r;
        setD({ ...ATT_VUOTA, ...att, id_cliente: att.id_cliente || '', id_assegnato: att.id_assegnato || '' });
        setSotto(sottoattivita || []);
        setPuoModificare(!!puo_modificare);
      } catch (e) {
        if (attivo) setErrore(e.message || 'Errore di caricamento');
      } finally {
        if (attivo) setLoading(false);
      }
    })();
    return () => { attivo = false; };
  }, [id, nuova]);

  const caricaAllegati = useCallback(async () => {
    if (nuova) return;
    try {
      const res = await fetch(`/api/attivita/${id}/allegati`, { headers: authHeaders() });
      const j = await res.json();
      if (j.success) setAllegati(j.data || []);
    } catch (e) { /* l'elenco resta quello precedente */ }
  }, [id, nuova]);

  useEffect(() => { caricaAllegati(); }, [caricaAllegati]);

  // ── Avvisi sulle mail di assegnazione ──
  const avvisaNotifiche = (lista) => {
    const l = (lista || []).filter(Boolean);
    const inviate  = l.filter(n => n.inviata);
    const problemi = l.filter(n => !n.inviata && n.motivo);
    if (inviate.length) addToast(inviate.length === 1 ? `Mail inviata a ${inviate[0].a}` : `${inviate.length} mail inviate`, 'success');
    if (problemi.length) addToast(`Salvato, ma mail non inviata: ${problemi[0].motivo}${problemi.length > 1 ? ` (+${problemi.length - 1})` : ''}`, 'error');
  };

  // ── Allegati ──
  const inviaFile = async (idAtt, files) => {
    let errori = 0;
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append('file', file);
        const res = await fetch(`/api/attivita/${idAtt}/allegati`, { method: 'POST', headers: authHeaders(), body: fd });
        const j = await res.json();
        if (!j.success) throw new Error(j.error || 'Errore upload');
      } catch (e) { errori++; }
    }
    return errori;
  };

  const aggiungiFile = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (nuova) { setInAttesa(p => [...p, ...files]); return; }
    setUploading(true);
    const errori = await inviaFile(id, files);
    setUploading(false);
    await caricaAllegati();
    if (errori) addToast(`${errori} allegati non caricati`, 'error');
    else addToast(files.length === 1 ? 'Allegato caricato' : `${files.length} allegati caricati`, 'success');
  };

  const eliminaAllegato = async (a) => {
    if (!window.confirm(`Rimuovere l'allegato "${nomeAllegato(a)}"?`)) return;
    try {
      const res = await fetch(`/api/attivita/${id}/allegati/${a.id}`, { method: 'DELETE', headers: authHeaders() });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Errore');
      caricaAllegati();
    } catch (e) { addToast(e.message, 'error'); }
  };

  // ── Sottoattività ──
  const salvaSotto = async (s, patch) => {
    try {
      const r = await api.put(`/sottoattivita/${s.id}`, patch);
      avvisaNotifiche([r.notifica]);
    } catch (e) { addToast(e.message, 'error'); }
    caricaSotto();
  };

  const eliminaSotto = async (s) => {
    if (!window.confirm(`Eliminare la sottoattività "${s.titolo}"?`)) return;
    try { await api.delete(`/sottoattivita/${s.id}`); }
    catch (e) { addToast(e.message, 'error'); }
    caricaSotto();
  };

  const aggiungiSotto = async () => {
    const titolo = nuovaSotto.titolo.trim();
    if (!titolo) return;
    const corpoSotto = { titolo, id_assegnato: nuovaSotto.id_assegnato || null, data_scad: nuovaSotto.data_scad || null };
    if (nuova) {
      setSottoInAttesa(p => [...p, corpoSotto]);
    } else {
      try {
        const r = await api.post(`/attivita/${id}/sotto`, corpoSotto);
        avvisaNotifiche([r.notifica]);
      } catch (e) { addToast(e.message, 'error'); return; }
      caricaSotto();
    }
    setNuovaSotto({ titolo: '', id_assegnato: '', data_scad: '' });
  };

  // ── Salvataggio ──
  const corpo = () => ({
    id_cliente: d.id_cliente || null, titolo: d.titolo, descrizione: d.descrizione || null,
    assegnata_a: d.assegnata_a || null, id_assegnato: d.id_assegnato || null,
    data_inizio: d.data_inizio || null, data_scad: d.data_scad || null,
    perc_compl: d.perc_compl || 0, stato: d.stato, note: d.note || null,
  });

  const salva = async (e) => {
    e.preventDefault();
    if (!String(d.titolo || '').trim() || bloccato) return;
    setSaving(true);
    try {
      const res = await fetch(nuova ? '/api/attivita' : `/api/attivita/${id}`, {
        method: nuova ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(corpo()),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Errore nel salvataggio');
      const notifiche = [j.data.notifica];
      if (nuova) {
        for (const sa of sottoInAttesa) {
          try { const r = await api.post(`/attivita/${j.data.id}/sotto`, sa); notifiche.push(r.notifica); }
          catch (err2) { addToast(`Sottoattività "${sa.titolo}" non salvata: ${err2.message}`, 'error'); }
        }
        if (inAttesa.length) {
          const errori = await inviaFile(j.data.id, inAttesa);
          if (errori) addToast(`${errori} allegati non caricati`, 'error');
        }
      }
      addToast(nuova ? 'Attività creata' : 'Attività aggiornata', 'success');
      avvisaNotifiche(notifiche);
      navigate('/attivita');
    } catch (err) {
      addToast(err.message, 'error');
      setSaving(false);
    }
  };

  const elimina = async () => {
    try {
      const res = await fetch(`/api/attivita/${id}`, { method: 'DELETE', headers: authHeaders() });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Errore');
      addToast('Attività eliminata', 'success');
      navigate('/attivita');
    } catch (err) { addToast(err.message, 'error'); }
  };

  if (loading) return <div className="page-content"><LoadingSpinner /></div>;
  if (errore) return (
    <div className="page-content">
      <button className="btn btn-sm" onClick={() => navigate('/attivita')}>← Attività</button>
      <div className="card" style={{ marginTop: 16 }}>
        <div className="empty-state"><div className="empty-state-title">{errore}</div></div>
      </div>
    </div>
  );

  const elencoFile = nuova ? inAttesa : allegati;
  const righeSotto = nuova ? sottoInAttesa : sotto;
  const fatte = sotto.filter(s => s.stato === 'COMPLETATA').length;
  const nomeUtenteDa = (uid) => utenti.find(u => String(u.id) === String(uid))?.nome || '—';

  return (
    <div className="page-content">
      <form onSubmit={salva}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-sm" onClick={() => navigate('/attivita')}>← Attività</button>
          <div style={{ fontSize: 17, fontWeight: 700, flex: 1, minWidth: 0 }}>
            {nuova ? 'Nuova attività' : (d.titolo || 'Modifica attività')}
          </div>
          {!nuova && puoModificare && (
            <button type="button" className="btn btn-danger btn-sm" onClick={() => setConfermaDel(true)}>🗑 Elimina</button>
          )}
          <button type="button" className="btn" onClick={() => navigate('/attivita')}>{bloccato ? 'Chiudi' : 'Annulla'}</button>
          {!bloccato && <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '⏳ Salvo…' : 'Salva'}</button>}
        </div>

        {bloccato && (
          <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 'var(--radius-sm)', fontSize: 13,
            background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
            Ti è stata assegnata una sottoattività di questa attività: puoi aggiornarne lo stato e aggiungere allegati.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(280px,1fr)', gap: 16, alignItems: 'start' }}>
          <div>
            {/* Dati attività */}
            <div className="card">
              <div className="card-header"><span className="card-title">Dati attività</span></div>
              <div className="card-body">
                <fieldset disabled={bloccato} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                  <div className="form-grid">
                    <div className="form-group form-full">
                      <label className="form-label">Titolo *</label>
                      <input className="form-control" value={d.titolo || ''} onChange={e => set('titolo', e.target.value)} required autoFocus={nuova} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Cliente</label>
                      <select className="form-control" value={d.id_cliente} onChange={e => set('id_cliente', e.target.value)}>
                        <option value="">— Nessuno —</option>
                        {clienti.map(c => <option key={c.id} value={c.id}>{clienteNome(c)}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Assegnata a</label>
                      <SelectUtente value={d.id_assegnato} utenti={utenti}
                        onChange={v => {
                          const u = utenti.find(x => String(x.id) === String(v));
                          setD(p => ({ ...p, id_assegnato: v, assegnata_a: u ? u.nome : '' }));
                        }} />
                      {d.assegnata_a && !d.id_assegnato && (
                        <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>
                          Valore precedente: "{d.assegnata_a}" (non collegato a un utente)
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Data inizio</label>
                      <input className="form-control" type="date" value={d.data_inizio || ''} onChange={e => set('data_inizio', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Scadenza</label>
                      <input className="form-control" type="date" value={d.data_scad || ''} onChange={e => set('data_scad', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Stato</label>
                      <select className="form-control" value={d.stato} onChange={e => set('stato', e.target.value)}>
                        {STATI.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        {d.stato === 'ARCHIVIATA' && <option value="ARCHIVIATA">Archiviata</option>}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Avanzamento: <strong>{d.perc_compl || 0}%</strong></label>
                      <input type="range" min="0" max="100" step="5" value={d.perc_compl || 0}
                        onChange={e => set('perc_compl', parseInt(e.target.value))}
                        style={{ width: '100%', accentColor: 'var(--accent)' }} />
                    </div>
                    <div className="form-group form-full">
                      <label className="form-label">Note</label>
                      <textarea className="form-control" rows={8} value={d.note || ''} onChange={e => set('note', e.target.value)} />
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>

            {/* Sottoattività */}
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-header">
                <span className="card-title">
                  ☑ Sottoattività {righeSotto.length > 0 && (nuova ? `(${righeSotto.length})` : `(${fatte}/${righeSotto.length} completate)`)}
                </span>
              </div>
              <div className="card-body">
                {righeSotto.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-faint)', fontStyle: 'italic', marginBottom: 10 }}>
                    Nessuna sottoattività. Puoi assegnarle ad altri utenti: riceveranno una mail.
                  </div>
                )}

                {nuova ? sottoInAttesa.map((s, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 110px 28px', gap: 8,
                    alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.titolo}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{s.id_assegnato ? nomeUtenteDa(s.id_assegnato) : '—'}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{s.data_scad ? formatDate(s.data_scad) : '—'}</span>
                    <button type="button" className="btn btn-sm btn-danger btn-icon" title="Rimuovi"
                      onClick={() => setSottoInAttesa(p => p.filter((_, j) => j !== i))}>×</button>
                  </div>
                )) : sotto.map(s => (
                  <SottoRiga key={s.id} s={s} utenti={utenti} gestore={puoModificare}
                    onSalva={salvaSotto} onElimina={eliminaSotto} />
                ))}

                {(nuova || puoModificare) && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 128px auto', gap: 8, marginTop: 12, alignItems: 'center' }}>
                    <input className="form-control" placeholder="Nuova sottoattività…" value={nuovaSotto.titolo}
                      onChange={e => setNuovaSotto(p => ({ ...p, titolo: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); aggiungiSotto(); } }} />
                    <SelectUtente value={nuovaSotto.id_assegnato} utenti={utenti}
                      onChange={v => setNuovaSotto(p => ({ ...p, id_assegnato: v }))} />
                    <input className="form-control" type="date" value={nuovaSotto.data_scad}
                      onChange={e => setNuovaSotto(p => ({ ...p, data_scad: e.target.value }))} />
                    <button type="button" className="btn btn-primary" disabled={!nuovaSotto.titolo.trim()} onClick={aggiungiSotto}>+ Aggiungi</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Allegati */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">📎 Allegati ({elencoFile.length})</span>
            </div>
            <div className="card-body">
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); aggiungiFile(e.dataTransfer.files); }}
                style={{ border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border-strong)'}`,
                  borderRadius: 'var(--radius-sm)', padding: 16, textAlign: 'center', cursor: 'pointer',
                  background: dragOver ? 'var(--accent-light)' : 'var(--bg)', marginBottom: 12,
                  fontSize: 13, color: 'var(--text-faint)' }}>
                <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
                  onChange={e => { aggiungiFile(e.target.files); e.target.value = ''; }} />
                {uploading ? '⏳ Caricamento…' : 'Trascina qui i file oppure clicca per sceglierli'}
              </div>

              {nuova && inAttesa.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 8 }}>
                  I file verranno caricati al salvataggio.
                </div>
              )}

              {elencoFile.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-faint)', fontStyle: 'italic', textAlign: 'center' }}>Nessun allegato</div>
              ) : elencoFile.map((a, i) => (
                <div key={nuova ? `${a.name}-${i}` : a.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', marginBottom: 6,
                    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
                  {nuova ? (
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {a.name}</span>
                  ) : (
                    <a href={urlAllegato(a)} target="_blank" rel="noreferrer"
                      style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--accent)', textDecoration: 'none',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      📎 {nomeAllegato(a)}
                    </a>
                  )}
                  <button type="button" className="btn btn-sm btn-danger btn-icon" title="Rimuovi"
                    onClick={() => nuova ? setInAttesa(p => p.filter((_, j) => j !== i)) : eliminaAllegato(a)}>×</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </form>

      {confermaDel && (
        <ConfirmDialog message={`Eliminare "${d.titolo}" e le sue sottoattività?`} onConfirm={elimina} onCancel={() => setConfermaDel(false)} />
      )}
    </div>
  );
}

// ─── RIGA ATTIVITÀ (espandibile) ─────────────────────────────────────────────
function RigaAttivita({ att, clienti, expanded, onToggle, onEdit, onDelete, onArchivia, selected, onSelect, onCreaCalendario }) {
  const diff   = diffGiorni(att.data_scad);
  const scad   = diff !== null && diff < 0 && att.stato !== 'COMPLETATA';
  const urg    = diff !== null && diff >= 0 && diff <= 3 && att.stato !== 'COMPLETATA';
  const si     = statoInfo(att.stato);
  const nomeC  = clienteNome(att);
  const [allegati, setAllegati] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (expanded && att.id) {
      setLoadingAll(true);
      fetch(`/api/attivita/${att.id}/allegati`, {
        headers:{ Authorization: 'Bearer ' + (localStorage.getItem('sp_token')||'') }
      }).then(r=>r.json()).then(j=>{ if(j.success) setAllegati(j.data||[]); }).finally(()=>setLoadingAll(false));
    }
  }, [expanded, att.id]);

  const uploadAllegati = async (files) => {
    for (const file of files) {
      const fd = new FormData(); fd.append('file', file);
      await fetch(`/api/attivita/${att.id}/allegati`, {
        method:'POST', headers:{ Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') }, body:fd
      });
    }
    // Reload allegati
    fetch(`/api/attivita/${att.id}/allegati`, {
      headers:{ Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') }
    }).then(r=>r.json()).then(j=>{ if(j.success) setAllegati(j.data||[]); });
  };

  const fileUrl = (f) => {
    const fn = f.filename || f.relpath || '';
    if (fn && !fn.includes('.') && fn.length > 20) return `https://drive.google.com/file/d/${fn}/view`;
    return `/uploads/${fn}`;
  };

  return (
    <>
      <div style={{
        display:'grid', gridTemplateColumns:'28px 28px 1fr 130px 110px 90px 90px 90px',
        gap:6, padding:'8px 12px', borderBottom:'1px solid var(--border)',
        background: scad ? 'rgba(239,68,68,0.05)' : urg ? 'rgba(245,158,11,0.05)' : 'var(--surface)',
        alignItems:'center',
      }}>
        {/* Checkbox */}
        <input type="checkbox" checked={selected} onChange={onSelect}
          style={{ accentColor:'var(--accent)', cursor:'pointer' }} />
        {/* Toggle */}
        <span style={{ cursor:'pointer', fontSize:11, color:'var(--text-faint)', textAlign:'center' }}
          onClick={onToggle}>{expanded?'▾':'▸'}</span>
        {/* Titolo */}
        <div style={{ cursor:'pointer', minWidth:0 }} onClick={onToggle}>
          <div style={{ fontWeight:500, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {scad && <span style={{color:'var(--c-red)', marginRight:4}}>⚠</span>}
            {urg  && <span style={{color:'var(--c-amber)', marginRight:4}}>🔔</span>}
            {att.titolo}
            {att.n_sotto > 0 && (
              <span title="Sottoattività completate" style={{ marginLeft:8, fontSize:10.5, fontWeight:600, color:'var(--text-faint)',
                background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'0 6px' }}>
                ☑ {att.n_sotto_fatte}/{att.n_sotto}
              </span>
            )}
          </div>
          {nomeC && <div style={{ fontSize:11, color:'var(--text-faint)' }}>{nomeC}</div>}
        </div>
        {/* Assegnata */}
        <div style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {att.assegnata_a || '—'}
        </div>
        {/* Scadenza */}
        <div style={{ fontSize:12, color: scad?'var(--c-red)':urg?'var(--c-amber)':'var(--text-muted)' }}>
          {att.data_scad ? formatDate(att.data_scad) : '—'}
        </div>
        {/* Stato */}
        <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:600,
          background: si.color+'22', color: si.color, whiteSpace:'nowrap' }}>{si.label}</span>
        {/* Avanzamento */}
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <div style={{ flex:1, height:4, background:'var(--border)', borderRadius:2 }}>
            <div style={{ width:`${att.perc_compl||0}%`, height:'100%', borderRadius:2,
              background: (att.perc_compl||0)===100?'var(--c-green)':'var(--accent)' }} />
          </div>
          <span style={{ fontSize:10, color:'var(--text-faint)', whiteSpace:'nowrap' }}>{att.perc_compl||0}%</span>
        </div>
        {/* Azioni */}
        <div style={{ display:'flex', gap:4 }} onClick={e=>e.stopPropagation()}>
          <button className="btn btn-sm btn-icon" onClick={()=>onEdit(att)} title="Modifica">✎</button>
          <button className="btn btn-sm btn-icon" onClick={()=>onCreaCalendario(att)} title="Crea su Calendario Google">📅</button>
          <button className="btn btn-sm btn-danger btn-icon" onClick={()=>onDelete(att)}>×</button>
        </div>
      </div>

      {/* Dettaglio espanso */}
      {expanded && (
        <div style={{ padding:'12px 16px 14px 70px', background:'var(--bg)', borderBottom:'1px solid var(--border)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:12 }}>
            <div>
              <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:2 }}>Data inizio</div>
              <div style={{ fontSize:13 }}>{att.data_inizio ? formatDate(att.data_inizio) : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:2 }}>Cliente</div>
              <div style={{ fontSize:13 }}>{nomeC || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:2 }}>Assegnata a</div>
              <div style={{ fontSize:13 }}>{att.assegnata_a || '—'}</div>
            </div>
          </div>
          {att.note && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:4 }}>Note / Corpo email</div>
              <div style={{ fontSize:13, lineHeight:1.6, whiteSpace:'pre-wrap', color:'var(--text)',
                background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'8px 12px',
                maxHeight:200, overflowY:'auto' }}>{att.note}</div>
            </div>
          )}
          {/* Allegati */}
          <div>
            <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:6, display:'flex', alignItems:'center', gap:8 }}>
              📎 Allegati ({allegati.length})
              <button onClick={()=>fileRef.current?.click()}
                style={{ fontSize:11, padding:'2px 8px', background:'var(--accent)', color:'white', border:'none', borderRadius:4, cursor:'pointer' }}>
                + Allega
              </button>
              <input ref={fileRef} type="file" multiple style={{ display:'none' }}
                onChange={e=>{ if(e.target.files.length) uploadAllegati(Array.from(e.target.files)); e.target.value=''; }} />
            </div>
            {loadingAll ? <div style={{ fontSize:11, color:'var(--text-faint)' }}>⏳</div>
            : allegati.length === 0 ? <div style={{ fontSize:11, color:'var(--text-faint)', fontStyle:'italic' }}>Nessun allegato</div>
            : (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {allegati.map((a,i) => (
                  <a key={i} href={fileUrl(a)} target="_blank" rel="noreferrer"
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 10px',
                      background:'var(--surface)', border:'1px solid var(--border)', borderRadius:6,
                      fontSize:12, color:'var(--accent)', textDecoration:'none' }}>
                    📎 {a.nome || a.filename?.split('/').pop()?.replace(/^\d+_/,'') || 'file'}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── MINI CALENDARIO ─────────────────────────────────────────────────────────
function MiniCalendario({ attivita }) {
  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth());

  const primoGiorno = new Date(anno, mese, 1).getDay();
  const giorniMese  = new Date(anno, mese+1, 0).getDate();
  const offsetLun   = (primoGiorno + 6) % 7; // lunedì = 0

  // Scadenze per giorno
  const scadenzeMap = {};
  attivita.forEach(a => {
    if (a.data_scad) {
      const d = new Date(a.data_scad);
      if (d.getFullYear()===anno && d.getMonth()===mese) {
        const k = d.getDate();
        if (!scadenzeMap[k]) scadenzeMap[k] = [];
        scadenzeMap[k].push(a);
      }
    }
  });

  const nomiMesi = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:12, fontSize:12 }}>
      {/* Header mese */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <button onClick={()=>{ if(mese===0){setMese(11);setAnno(a=>a-1);}else setMese(m=>m-1); }}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color:'var(--text-muted)' }}>‹</button>
        <span style={{ fontWeight:600, color:'var(--text)' }}>{nomiMesi[mese]} {anno}</span>
        <button onClick={()=>{ if(mese===11){setMese(0);setAnno(a=>a+1);}else setMese(m=>m+1); }}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color:'var(--text-muted)' }}>›</button>
      </div>
      {/* Intestazione giorni */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:4 }}>
        {['L','M','M','G','V','S','D'].map((g,i)=>(
          <div key={i} style={{ textAlign:'center', fontSize:10, fontWeight:700, color:'var(--text-faint)', padding:'2px 0' }}>{g}</div>
        ))}
      </div>
      {/* Giorni */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
        {Array.from({length:offsetLun}).map((_,i)=><div key={'e'+i}/>)}
        {Array.from({length:giorniMese}).map((_,i)=>{
          const giorno = i+1;
          const isOggi = anno===oggi.getFullYear() && mese===oggi.getMonth() && giorno===oggi.getDate();
          const hasScad = scadenzeMap[giorno];
          return (
            <div key={giorno} title={hasScad ? hasScad.map(a=>a.titolo).join('\n') : ''}
              style={{ textAlign:'center', padding:'3px 0', borderRadius:4, fontSize:11,
                background: isOggi ? 'var(--accent)' : hasScad ? 'var(--c-amber)' : 'transparent',
                color: isOggi ? 'white' : hasScad ? 'white' : 'var(--text)',
                fontWeight: isOggi || hasScad ? 700 : 400,
                cursor: hasScad ? 'pointer' : 'default',
                position:'relative',
              }}>
              {giorno}
              {hasScad && hasScad.length > 1 && (
                <div style={{ position:'absolute', top:-2, right:-2, background:'var(--c-red)', color:'white',
                  borderRadius:'50%', width:12, height:12, fontSize:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {hasScad.length}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Legenda */}
      <div style={{ display:'flex', gap:10, marginTop:8, fontSize:10, color:'var(--text-faint)' }}>
        <span><span style={{ display:'inline-block', width:8, height:8, borderRadius:2, background:'var(--accent)', marginRight:3 }}/>Oggi</span>
        <span><span style={{ display:'inline-block', width:8, height:8, borderRadius:2, background:'var(--c-amber)', marginRight:3 }}/>Scadenza</span>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Attivita({ addToast }) {
  const navigate = useNavigate();
  const [rows, setRows]           = useState([]);
  const [clienti, setClienti]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filtroStato, setFiltroStato] = useState('TUTTI');
  const [showArchiviate, setShowArchiviate] = useState(false);
  const [delItem, setDelItem]     = useState(null);
  const [q, setQ]                 = useState('');
  const [selected, setSelected]   = useState(new Set());
  const [expanded, setExpanded]   = useState(new Set());

  useEffect(() => {
    api.getClienti({ limit:300 }).then(d => setClienti(Array.isArray(d) ? d : (d?.rows||[])));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = showArchiviate ? { archiviate:'1' } : {};
    fetch(`/api/attivita?${new URLSearchParams(params)}`, {
      headers:{ Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') }
    })
      .then(r=>r.json()).then(j=>{ if(j.success) setRows(j.data||[]); })
      .finally(() => setLoading(false));
  }, [showArchiviate]);

  useEffect(() => { load(); }, [load]);

  // Filtri
  const filtered = rows.filter(r => {
    if (filtroStato !== 'TUTTI' && r.stato !== filtroStato) return false;
    if (q) {
      const txt = [r.titolo, r.note, r.assegnata_a, clienteNome(r)].join(' ').toLowerCase();
      if (!txt.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const handleDelete = async () => {
    try {
      await fetch(`/api/attivita/${delItem.id}`, {
        method:'DELETE', headers:{ Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') }
      });
      addToast('Attività eliminata','success');
      setDelItem(null); load();
    } catch(e) { addToast(e.message,'error'); }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Eliminare ${selected.size} attività selezionate?`)) return;
    try {
      await Promise.all([...selected].map(id =>
        fetch(`/api/attivita/${id}`, { method:'DELETE', headers:{ Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') } })
      ));
      addToast(`${selected.size} attività eliminate`, 'success');
      setSelected(new Set()); load();
    } catch(e) { addToast(e.message,'error'); }
  };

  const handleCreaCalendario = (att) => {
    const title = encodeURIComponent(att.titolo);
    const start = att.data_scad ? att.data_scad.replace(/-/g,'') : new Date().toISOString().slice(0,10).replace(/-/g,'');
    const details = encodeURIComponent(att.note || '');
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${start}&details=${details}`;
    window.open(url, '_blank');
  };

  const toggleExpanded = (id) => setExpanded(p => { const n=new Set(p); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleSelected = (id) => setSelected(p => { const n=new Set(p); n.has(id)?n.delete(id):n.add(id); return n; });
  const selectAll  = () => setSelected(new Set(filtered.map(r=>r.id)));
  const clearSelect = () => setSelected(new Set());

  const stats = {
    aperte:     rows.filter(r=>r.stato==='APERTA').length,
    inCorso:    rows.filter(r=>r.stato==='IN_CORSO').length,
    completate: rows.filter(r=>r.stato==='COMPLETATA').length,
    sospese:    rows.filter(r=>r.stato==='SOSPESA').length,
  };

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        <div style={{ fontSize:17, fontWeight:700, flex:1 }}>{showArchiviate ? 'Archivio Attività' : 'Attività'}</div>
        <div className="search-box" style={{ width:200 }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Cerca…" value={q} onChange={e=>setQ(e.target.value)} />
        </div>
        {selected.size > 0 && (
          <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected}>🗑 Elimina {selected.size}</button>
        )}
        <button className={`btn btn-sm ${showArchiviate?'btn-primary':''}`} onClick={()=>setShowArchiviate(s=>!s)}>📦 Archivio</button>
        {!showArchiviate && <button className="btn btn-primary" onClick={()=>navigate('/attivita/nuova')}>+ Nuova</button>}
      </div>

      {/* Filtri + KPI compatti in una sola riga */}
      <div style={{ display:'flex', gap:6, marginBottom:10, alignItems:'center', flexWrap:'wrap' }}>
        {[
          ['TUTTI', 'Tutte', null, rows.length],
          ['APERTA', 'Aperte', 'var(--c-red)', stats.aperte],
          ['IN_CORSO', 'In corso', 'var(--c-amber)', stats.inCorso],
          ['COMPLETATA', 'Completate', 'var(--c-green)', stats.completate],
          ['SOSPESA', 'Sospese', 'var(--text-faint)', stats.sospese],
        ].map(([k,l,col,n])=>{
          const active = filtroStato===k;
          return (
            <button key={k} onClick={()=>setFiltroStato(k)}
              style={{ padding:'4px 10px', borderRadius:20, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:5,
                border:`1.5px solid ${active?(col||'var(--accent)'):'var(--border-strong)'}`,
                background: active?(col||'var(--accent)'):'transparent',
                color: active?'white':'var(--text-muted)', fontWeight:500 }}>
              {l}
              <span style={{ background:active?'rgba(255,255,255,0.25)':(col?col+'22':'var(--bg)'),
                color:active?'white':(col||'var(--text-muted)'),
                borderRadius:10, padding:'0 5px', fontSize:10, fontWeight:700 }}>{n}</span>
            </button>
          );
        })}
        {selected.size > 0 && (
          <span style={{ marginLeft:'auto', fontSize:12, color:'var(--text-faint)' }}>
            {selected.size} selezionati
            <button onClick={clearSelect} style={{ marginLeft:6, background:'none', border:'none', cursor:'pointer', color:'var(--c-red)', fontSize:12 }}>✕</button>
          </span>
        )}
      </div>

      {/* Tabella attività */}
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
        {/* Intestazione */}
        <div style={{ display:'grid', gridTemplateColumns:'28px 28px 1fr 130px 110px 90px 90px 90px',
          gap:6, padding:'7px 12px', background:'var(--bg)', borderBottom:'2px solid var(--border)',
          fontSize:10.5, fontWeight:700, color:'var(--text-faint)', textTransform:'uppercase', letterSpacing:'0.4px' }}>
          <input type="checkbox" onChange={e=>e.target.checked?selectAll():clearSelect()}
            checked={selected.size>0 && selected.size===filtered.length}
            style={{ accentColor:'var(--accent)', cursor:'pointer' }} />
          <div></div>
          <div>Attività</div>
          <div>Assegnata a</div>
          <div>Scadenza</div>
          <div>Stato</div>
          <div>Avanzamento</div>
          <div></div>
        </div>

        {loading ? <div style={{ padding:32 }}><LoadingSpinner /></div>
        : filtered.length === 0 ? (
          <div className="empty-state" style={{ padding:32 }}>
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">Nessuna attività trovata</div>
          </div>
        ) : filtered.map(att => (
          <RigaAttivita key={att.id} att={att} clienti={clienti}
            expanded={expanded.has(att.id)}
            onToggle={() => toggleExpanded(att.id)}
            onEdit={a => navigate(`/attivita/${a.id}`)}
            onDelete={setDelItem}
            onArchivia={() => {}}
            selected={selected.has(att.id)}
            onSelect={() => toggleSelected(att.id)}
            onCreaCalendario={handleCreaCalendario} />
        ))}
      </div>

      {/* Conferma eliminazione */}
      {delItem && <ConfirmDialog message={`Eliminare "${delItem.titolo}"?`}
        onConfirm={handleDelete} onCancel={()=>setDelItem(null)} />}
    </div>
  );
}
